// src/pages/documentation/quick-start/QuickStartLogin.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faLock,
    faSignInAlt,
    faCheckCircle,
    faArrowRight,
    faArrowLeft,
    faKey,
    faInfoCircle,
    faClock,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const QuickStartLogin = () => {
    const loginSteps = [
        {
            step: 1,
            title: 'Accede a la página de inicio de sesión',
            description: 'Ve a /login o haz clic en "Iniciar sesión" desde la página principal',
            details: 'Si ya tienes cuenta, puedes iniciar sesión directamente.'
        },
        {
            step: 2,
            title: 'Ingresa tu email y contraseña',
            description: 'Escribe el email y contraseña que usaste al registrarte',
            details: 'Asegúrate de escribir correctamente tu email y contraseña.'
        },
        {
            step: 3,
            title: 'Verifica tu email si es necesario',
            description: 'Si tu email no está verificado, el sistema te pedirá verificarlo primero',
            details: 'Puedes solicitar que se reenvíe el email de verificación si no lo recibiste.'
        },
        {
            step: 4,
            title: 'Accede a tu dashboard',
            description: 'Una vez iniciada la sesión, serás redirigido automáticamente a tu dashboard',
            details: 'Si es tu primera vez, serás guiado al proceso de onboarding.'
        }
    ];

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-green-600 rounded-lg">
                            <FontAwesomeIcon icon={faSignInAlt} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Paso 2: Iniciar sesión</h1>
                            <p className="text-gray-600">Accede a tu cuenta de PetitPrime</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/quick-start" className="hover:text-gray-700">Inicio Rápido</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Login</span>
                    </div>
                </div>

                {/* Overview */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-green-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué vas a hacer?</h3>
                            <p className="text-gray-700 mb-3">
                                Una vez que hayas verificado tu email, podrás iniciar sesión en PetitPrime y acceder a tu cuenta.
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faClock} />
                                    Tiempo estimado: 2 minutos
                                </span>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    4 pasos simples
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-4 mb-8">
                    {loginSteps.map((step) => (
                        <div key={step.step} className="bg-white border border-gray-200 rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold">
                                    {step.step}
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{step.title}</h3>
                                    <p className="text-gray-600 mb-2">{step.description}</p>
                                    <p className="text-gray-500 text-sm italic">{step.details}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Email Verification */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faEnvelope} />
                        ¿No recibiste el email de verificación?
                    </h4>
                    <p className="text-blue-700 mb-3">
                        Si intentas iniciar sesión y tu email no está verificado, el sistema te mostrará una opción para reenviar el email de verificación.
                    </p>
                    <ol className="text-blue-700 space-y-2 list-decimal list-inside">
                        <li>Haz clic en "Sí, reenviar email" cuando aparezca el mensaje</li>
                        <li>Revisa tu bandeja de entrada (y la carpeta de spam)</li>
                        <li>Haz clic en el enlace de verificación del email</li>
                        <li>Vuelve a intentar iniciar sesión</li>
                    </ol>
                </div>

                {/* Password Recovery */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faKey} />
                        ¿Olvidaste tu contraseña?
                    </h4>
                    <p className="text-purple-700 mb-3">
                        Si olvidaste tu contraseña, puedes recuperarla fácilmente:
                    </p>
                    <ol className="text-purple-700 space-y-2 list-decimal list-inside">
                        <li>Haz clic en "¿Olvidaste tu contraseña?" en la página de inicio de sesión</li>
                        <li>Ingresa tu email</li>
                        <li>Revisa tu correo y sigue las instrucciones</li>
                        <li>Crea una nueva contraseña segura</li>
                    </ol>
                </div>

                {/* Account Types */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                    <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        Tipos de cuenta
                    </h4>
                    <p className="text-yellow-700 mb-3">
                        PetitPrime tiene diferentes tipos de cuenta según tu rol:
                    </p>
                    <ul className="text-yellow-700 space-y-2">
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span><strong>Dueño de negocio:</strong> Acceso completo a todas las funcionalidades</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span><strong>Trabajador:</strong> Acceso limitado para gestionar clientes en una tienda específica</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span><strong>Franquiciado:</strong> Acceso para gestionar múltiples tiendas de una franquicia</span>
                        </li>
                    </ul>
                    <p className="text-yellow-700 mt-3 text-sm">
                        Cada tipo de cuenta tiene su propia página de inicio de sesión. Si eres dueño de negocio, usa la página principal de login.
                    </p>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/quick-start/registro"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Anterior: Crear Cuenta
                    </Link>
                    <Link
                        to="/documentation/quick-start/onboarding"
                        className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                        Siguiente: Configurar Negocio
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default QuickStartLogin;

